import { ArchiveError } from '../app/errors';
import type {
  AnnotationRow,
  CoachCardRow,
  FlagResolutionRow,
  HypothesisRow,
  MatchNoteRow,
  MatchRow,
  ObservationRow,
  OurPlayerRow,
  PlayerRow,
  SetPieceRow,
  SettingRow,
  TalkPointRow,
  TeamRow,
  TemplateRow,
} from '../types';
import type { ArchiveAudioBlobMeta, ArchiveEntity } from './fields';
import { settingDisposition } from './fields';

/**
 * **SYN-4's two archive scopes, plus BAK-1's whole-database one — decided in one place.**
 *
 * There are three scopes and they are *not* the same shape:
 *
 * | Scope | Contents | Written by |
 * |---|---|---|
 * | `match` | "the Match row and its Hypotheses, Observations including audio blobs, SetPieces, TalkPoints and MatchNotes including note audio, plus the Team and Player rows it references" (SYN-4) | the SYN-1 pushes (§11.1) |
 * | `profile` | "a separate small profile archive covering Templates and Settings" (SYN-4) | any command mutating a Template or a Setting |
 * | `all` | everything archivable — BAK-1's "full JSON dump plus all audio blobs", and §13.5's recovery export | the manual export |
 *
 * INV-12 is a per-scope property ("byte-identical to the archive the same client would produce
 * locally **for the same scope**"), which is only checkable if "the same scope" means one thing. It
 * means this function. A drainer that hand-filtered rows by `matchId` and a BAK-1 export that walked
 * whole tables would be two definitions of a match archive, and they would agree right up until one
 * of them was extended.
 *
 * **Pure, and defensive about its input.** The worker (§11.2) will read a match's rows by index
 * rather than loading the profile, which is a performance choice, not a semantic one: this function
 * re-applies every membership rule, so a pre-narrowed input and a whole-profile input produce the
 * same result. Passing more than is needed is always safe; passing less is what would silently
 * shrink an archive, and the counts in the manifest are what would show it.
 */

export type ArchiveScope =
  | { readonly kind: 'match'; readonly matchId: string }
  | { readonly kind: 'profile' }
  | { readonly kind: 'all' };

export type ArchiveScopeKind = ArchiveScope['kind'];

/**
 * Every row the serializer may draw on, as plain arrays.
 *
 * Typed against `types/` — `archive/` may not import `data/`, so this is a structural contract that
 * `data/repos/*` results satisfy without either layer knowing about the other. `audioBlobs` carries
 * metadata only; the bytes arrive through `ArchiveBlobReader` (§3.1's boundary rule).
 */
export interface ArchiveRows {
  readonly teams: readonly TeamRow[];
  readonly players: readonly PlayerRow[];
  readonly matches: readonly MatchRow[];
  readonly hypotheses: readonly HypothesisRow[];
  readonly observations: readonly ObservationRow[];
  readonly flagResolutions: readonly FlagResolutionRow[];
  readonly setPieces: readonly SetPieceRow[];
  readonly talkPoints: readonly TalkPointRow[];
  readonly matchNotes: readonly MatchNoteRow[];
  readonly annotations: readonly AnnotationRow[];
  readonly coachCards: readonly CoachCardRow[];
  readonly ourPlayers: readonly OurPlayerRow[];
  readonly audioBlobs: readonly ArchiveAudioBlobMeta[];
  readonly templates: readonly TemplateRow[];
  readonly settings: readonly SettingRow[];
}

/**
 * Which entities each scope's `data.json` carries.
 *
 * An entity listed here appears **even when it has no rows**, as an empty array. The shape of an
 * archive is then a function of its scope alone, which is what lets an importer decide what it is
 * looking at from the manifest rather than from which keys happen to be present.
 */
export const SCOPE_ENTITIES: Readonly<Record<ArchiveScopeKind, readonly ArchiveEntity[]>> = {
  match: [
    'teams',
    'players',
    'matches',
    'hypotheses',
    'observations',
    'flagResolutions',
    'setPieces',
    'talkPoints',
    'matchNotes',
    // B-1: the two match-scoped B entities travel with their match. `ourPlayers` is
    // deliberately NOT here — the roster is profile data and a per-match push has no claim
    // on it (and no `matchId` to select it by).
    'annotations',
    'coachCards',
    'audioBlobs',
  ],
  // B-1: the roster restores through the profile archive, the same channel as templates and
  // settings — SYN-5 pulls the profile archive plus every match archive, so a profile-scoped
  // row that lived only in the `all` scope would be silently lost on restore.
  profile: ['templates', 'settings', 'ourPlayers'],
  all: [
    'teams',
    'players',
    'matches',
    'hypotheses',
    'observations',
    'flagResolutions',
    'setPieces',
    'talkPoints',
    'matchNotes',
    'annotations',
    'coachCards',
    'audioBlobs',
    'templates',
    'settings',
    'ourPlayers',
  ],
};

export interface ScopedArchive {
  readonly scope: ArchiveScope;
  /** In `SCOPE_ENTITIES` order, unsorted within an entity — `serialize.ts` applies the ULID sort. */
  readonly entities: Partial<Record<ArchiveEntity, readonly unknown[]>>;
  /** Audio metadata rows in scope, ascending by id — the order blobs are read in (§11.2). */
  readonly blobIds: readonly string[];
  /** Setting keys `fields.ts` denied. Reported, never silent (§11.8). */
  readonly excludedSettingKeys: readonly string[];
  /** `audioBlobId`s a row points at with no metadata row behind them — OBS-2's orphan condition. */
  readonly unknownBlobIds: readonly string[];
  /** Set when the match's `teamId` resolves to nothing. The archive is still built (§13.5). */
  readonly danglingTeamId: string | null;
}

function archivableSettings(settings: readonly SettingRow[]): {
  kept: SettingRow[];
  excluded: string[];
} {
  const kept: SettingRow[] = [];
  const excluded: string[] = [];
  for (const row of settings) {
    if (settingDisposition(row.key) === 'archive') kept.push(row);
    else excluded.push(row.key);
  }
  return { kept, excluded: excluded.sort() };
}

/** `audioBlobId`s referenced by the rows in scope, both sources, deduplicated. */
function referencedBlobIds(
  observations: readonly ObservationRow[],
  matchNotes: readonly MatchNoteRow[],
): Set<string> {
  const ids = new Set<string>();
  for (const row of observations) if (row.audioBlobId !== null) ids.add(row.audioBlobId);
  for (const row of matchNotes) if (row.audioBlobId !== null) ids.add(row.audioBlobId);
  return ids;
}

/**
 * @throws {ArchiveError} when a `match` scope names a match the source does not contain.
 *
 * This is the one selection failure that throws rather than reporting. An empty match archive is a
 * *valid* archive: the drainer would PUT it under the match's key and overwrite the server's copy
 * with nothing, and SYN-6 would then report the match as protected. BAK-4 is explicit that local
 * deletion must not delete the server archive; producing an empty one for a match that is gone
 * would do exactly that by another route.
 */
export function selectScope(rows: ArchiveRows, scope: ArchiveScope): ScopedArchive {
  if (scope.kind === 'profile') {
    const { kept, excluded } = archivableSettings(rows.settings);
    return {
      scope,
      entities: { templates: rows.templates, settings: kept, ourPlayers: rows.ourPlayers },
      blobIds: [],
      excludedSettingKeys: excluded,
      unknownBlobIds: [],
      danglingTeamId: null,
    };
  }

  if (scope.kind === 'all') {
    const { kept, excluded } = archivableSettings(rows.settings);
    const known = new Set(rows.audioBlobs.map((meta) => meta.id));
    const referenced = referencedBlobIds(rows.observations, rows.matchNotes);
    return {
      scope,
      entities: {
        teams: rows.teams,
        players: rows.players,
        matches: rows.matches,
        hypotheses: rows.hypotheses,
        observations: rows.observations,
        flagResolutions: rows.flagResolutions,
        setPieces: rows.setPieces,
        talkPoints: rows.talkPoints,
        matchNotes: rows.matchNotes,
        annotations: rows.annotations,
        coachCards: rows.coachCards,
        // BAK-1 is "a full JSON dump plus **all** audio blobs" — including any an orphan report
        // would flag, because an export that dropped them would destroy the only evidence.
        audioBlobs: rows.audioBlobs,
        templates: rows.templates,
        settings: kept,
        ourPlayers: rows.ourPlayers,
      },
      blobIds: rows.audioBlobs.map((meta) => meta.id).sort(),
      excludedSettingKeys: excluded,
      unknownBlobIds: [...referenced].filter((id) => !known.has(id)).sort(),
      danglingTeamId: null,
    };
  }

  const match = rows.matches.find((row) => row.id === scope.matchId);
  if (!match) {
    throw new ArchiveError(
      `No match ${scope.matchId} is present in the rows this archive was asked to cover.`,
      { context: { matchId: scope.matchId, matchesSupplied: rows.matches.length } },
    );
  }

  const ofMatch = <T extends { matchId: string }>(list: readonly T[]): T[] =>
    list.filter((row) => row.matchId === scope.matchId);

  const hypotheses = ofMatch(rows.hypotheses);
  const observations = ofMatch(rows.observations);
  const setPieces = ofMatch(rows.setPieces);
  const talkPoints = ofMatch(rows.talkPoints);
  const matchNotes = ofMatch(rows.matchNotes);
  const annotations = ofMatch(rows.annotations);
  const coachCards = ofMatch(rows.coachCards);

  // §3.1: "matchId is reached through the flag Observation, not duplicated." So membership is by
  // the observation set, not by a column — a second copy of the match id could disagree with the
  // first, and the resolution would follow the wrong match into the wrong archive.
  const observationIds = new Set(observations.map((row) => row.id));
  const flagResolutions = rows.flagResolutions.filter((row) => observationIds.has(row.observationId));

  const team = rows.teams.find((row) => row.id === match.teamId);
  // SYN-4 says "the Team and Player rows it references". Taken as **every player of that team**,
  // not only those a hypothesis names, and the reason is SYN-5: restore pulls the profile archive
  // (Templates and Settings) plus every match archive, so a player who appears in no hypothesis
  // would otherwise have no archive to come back from and would be silently lost on restore.
  const players = rows.players.filter((row) => row.teamId === match.teamId);

  const known = new Set(rows.audioBlobs.map((meta) => meta.id));
  const referenced = referencedBlobIds(observations, matchNotes);
  const audioBlobs = rows.audioBlobs.filter((meta) => referenced.has(meta.id));

  return {
    scope,
    entities: {
      teams: team ? [team] : [],
      players,
      matches: [match],
      hypotheses,
      observations,
      flagResolutions,
      setPieces,
      talkPoints,
      matchNotes,
      annotations,
      coachCards,
      audioBlobs,
    },
    blobIds: audioBlobs.map((meta) => meta.id).sort(),
    excludedSettingKeys: [],
    unknownBlobIds: [...referenced].filter((id) => !known.has(id)).sort(),
    danglingTeamId: team ? null : match.teamId,
  };
}
